/**
 * Course expansion — exact mirror of contracts/src/libraries/CourseLib.sol.
 */
import { encodeAbiParameters, keccak256, type Hex } from 'viem';
import { type CourseObject, EncounterKind, type FormationTemplate } from './types.js';

export const COURSE_START = 360n;
export const FINISH_OFFSET = 150n;
export const MAX_FORMATIONS = 6;

const ORDER_PARAMS = [{ type: 'bytes32' }, { type: 'string' }, { type: 'uint256' }] as const;

function word(seed: Hex, tag: string, i: number): bigint {
  return BigInt(keccak256(encodeAbiParameters(ORDER_PARAMS, [seed, tag, BigInt(i)])));
}

const clampX = (v: bigint, xMax: bigint): bigint => (v > xMax ? xMax : v < -xMax ? -xMax : v);

export function order(seed: Hex, n: number): number[] {
  if (n > MAX_FORMATIONS) throw new Error('TooManyFormations');
  const idx = Array.from({ length: n }, (_, i) => i);
  for (let i = 0; i + 1 < n; i++) {
    const r = word(seed, 'ORDER', i);
    const j = i + Number(r % BigInt(n - i));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
}

export function variation(seed: Hex, slot: number, f: FormationTemplate): { mirror: boolean; shift: bigint } {
  const v = word(seed, 'VARIATION', slot);
  const mirror = f.mirrorable && (v & 1n) === 1n;
  let shift = 0n;
  if (f.maxShift > 0n) {
    const span = 2n * f.maxShift + 1n;
    shift = ((v >> 8n) % span) - f.maxShift;
  }
  return { mirror, shift };
}

export function expand(seed: Hex, fs: FormationTemplate[], xMax: bigint): { course: CourseObject[]; length: bigint } {
  const ord = order(seed, fs.length);
  const course: CourseObject[] = [];
  let cursorY = COURSE_START;
  ord.forEach((fi, slot) => {
    const f = fs[fi];
    const { mirror, shift } = variation(seed, slot, f);
    for (const o of f.objects) {
      const xL = mirror ? -o.xR : o.xL;
      const xR = mirror ? -o.xL : o.xR;
      course.push({ kind: o.kind, y: cursorY + o.y, xL: clampX(xL + shift, xMax), xR: clampX(xR + shift, xMax), param: o.param });
    }
    cursorY += f.length;
  });
  course.push({ kind: EncounterKind.FINISH, y: cursorY + FINISH_OFFSET, xL: -xMax, xR: xMax, param: 0n });
  return { course, length: cursorY + FINISH_OFFSET };
}

/** keccak256(abi.encode(CourseObject[])) as computed by RunnerGame.startRun. */
export function courseHash(course: CourseObject[]): Hex {
  return keccak256(
    encodeAbiParameters(
      [
        {
          type: 'tuple[]',
          components: [
            { name: 'kind', type: 'uint8' },
            { name: 'y', type: 'uint32' },
            { name: 'xL', type: 'int32' },
            { name: 'xR', type: 'int32' },
            { name: 'param', type: 'uint32' },
          ],
        },
      ],
      [course.map((o) => ({ kind: o.kind, y: Number(o.y), xL: Number(o.xL), xR: Number(o.xR), param: Number(o.param) }))],
    ),
  );
}
